// Récupération d'itinéraires, avec repli sur plusieurs moteurs.
// Aucune dépendance au DOM : le journal est injecté par setRoutingLogger().

import { VALHALLA_MANEUVERS } from './maneuvers.js';

let log = () => {};
function setRoutingLogger(fn) { log = fn; }

const OSRM_ENDPOINTS = [
    'https://router.project-osrm.org/route/v1/driving/',
    'https://routing.openstreetmap.de/routed-car/route/v1/driving/'
];

const VALHALLA_ENDPOINTS = [
    'https://valhalla1.openstreetmap.de/route',
    'https://valhalla2.openstreetmap.de/route'
];

const ROUTE_TIMEOUT_MS = 25000;

const OSRM_MAX_WAYPOINTS = 20;   // limite du serveur public OSRM

// Tolérance autour du cap imposé. 45° : assez serré pour exclure le sens inverse, qui est à
// 180°, assez large pour absorber un tracé légèrement différent de celui d'origine.
const BEARING_TOLERANCE_DEG = 45;


function timeoutSignal(ms) {
    try { return AbortSignal.timeout(ms); } catch (e) { return undefined; }
}


async function fetchOsrmChunk(base, chunk, bearings) {
    const coords = chunk.map(wp => `${wp.lng},${wp.lat}`).join(';');
    // radiuses=unlimited : les arrêts enregistrés tombent souvent dans un
    // stationnement ou une entrée de cour. Sans ça OSRM refuse le point et
    // renvoie NoSegment.
    const radiuses = chunk.map(() => 'unlimited').join(';');
    let url = `${base}${coords}?steps=true&overview=full&geometries=geojson&radiuses=${radiuses}`;
    if (hasBearings(bearings)) {
        // bearings impose le SENS de circulation à chaque arrêt, approaches=curb impose d'y
        // arriver sans traverser la chaussée. Ensemble, ils garantissent que la porte du bus
        // donne sur le trottoir. Une entrée vide laisse le point libre.
        const b = chunk.map((wp, i) => {
            const deg = bearings && bearings[i];
            return Number.isFinite(deg) ? `${Math.round(deg)},${BEARING_TOLERANCE_DEG}` : '';
        }).join(';');
        const a = chunk.map((wp, i) => (Number.isFinite(bearings && bearings[i]) ? 'curb' : '')).join(';');
        url += `&bearings=${b}&approaches=${a}`;
    }
    const res = await fetch(url, { signal: timeoutSignal(ROUTE_TIMEOUT_MS) });
    const data = await res.json();
    if (data.code !== 'Ok' || !data.routes || !data.routes.length) {
        throw new Error(data.message || data.code || 'réponse invalide');
    }
    return data.routes[0];
}


function decodePolyline6(str) {
    const coords = [];
    let index = 0, lat = 0, lon = 0;
    while (index < str.length) {
        for (let i = 0; i < 2; i++) {
            let shift = 0, result = 0, byte;
            do {
                byte = str.charCodeAt(index++) - 63;
                result |= (byte & 0x1f) << shift;
                shift += 5;
            } while (byte >= 0x20);
            const delta = (result & 1) ? ~(result >> 1) : (result >> 1);
            if (i === 0) lat += delta; else lon += delta;
        }
        coords.push([lat / 1e6, lon / 1e6]);
    }
    return coords;
}


function normalizeValhalla(trip) {
    const geometry = [];
    const legs = trip.legs.map(leg => {
        const shape = decodePolyline6(leg.shape);
        shape.forEach(pt => geometry.push([pt[1], pt[0]]));   // OSRM attend [lng, lat]
        const steps = (leg.maneuvers || []).map(mv => {
            const pair = VALHALLA_MANEUVERS[mv.type] || ['continue', 'straight'];
            const pt = shape[mv.begin_shape_index] || shape[0] || [0, 0];
            return {
                name: (mv.street_names || []).join(', '),
                distance: Math.max(0, (mv.length || 0) * 1000),
                duration: Math.max(0, mv.time || 0),
                maneuver: {
                    type: pair[0],
                    modifier: pair[1],
                    location: [pt[1], pt[0]],
                    exit: mv.roundabout_exit_count
                }
            };
        });
        return { steps: steps };
    });
    return { geometry: { coordinates: geometry }, legs: legs };
}


async function fetchValhallaChunk(chunk, bearings) {
    const body = {
        // radius : équivalent Valhalla de radiuses=unlimited, pour accepter un arrêt
        // relevé dans un stationnement.
        // heading / preferred_side : équivalents Valhalla de bearings / approaches=curb.
        locations: chunk.map((wp, i) => {
            const loc = { lat: wp.lat, lon: wp.lng, type: 'break', radius: 100 };
            const deg = bearings && bearings[i];
            if (hasBearings(bearings) && Number.isFinite(deg)) {
                loc.heading = Math.round(deg);
                loc.heading_tolerance = BEARING_TOLERANCE_DEG;
                loc.preferred_side = 'same';
            }
            return loc;
        }),
        costing: 'auto',
        directions_options: { units: 'kilometers', language: 'fr-FR' }
    };
    let last = null;
    for (const ep of VALHALLA_ENDPOINTS) {
        try {
            const res = await fetch(ep, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                signal: timeoutSignal(ROUTE_TIMEOUT_MS)
            });
            if (!res.ok) { last = new Error('HTTP ' + res.status); continue; }
            const j = await res.json();
            if (!j.trip || j.trip.status !== 0 || !j.trip.legs || !j.trip.legs.length) {
                last = new Error((j.trip && j.trip.status_message) || 'réponse invalide');
                continue;
            }
            return normalizeValhalla(j.trip);
        } catch (e) { last = e; }
    }
    throw last || new Error('injoignable');
}


// Y a-t-il au moins une contrainte à poser ? Un tableau tout en null ne doit rien ajouter à
// la requête : les paramètres vides d'OSRM sont acceptés, mais autant ne pas les émettre.
function hasBearings(bearings) {
    return Array.isArray(bearings) && bearings.some(b => Number.isFinite(b));
}


async function tryEngines(chunk, bearings, errors) {
    for (const base of OSRM_ENDPOINTS) {
        try { return await fetchOsrmChunk(base, chunk, bearings); }
        catch (e) { errors.push(`${base.split('/')[2]} : ${e.message}`); }
    }
    log("Routage", "OSRM indisponible, bascule sur Valhalla.");
    try { return await fetchValhallaChunk(chunk, bearings); }
    catch (e) { errors.push(`valhalla : ${e.message}`); }
    return null;
}


async function fetchRouteChunk(chunk, bearings) {
    const errors = [];
    const route = await tryEngines(chunk, bearings, errors);
    if (route) return route;

    // Un cap trop serré fait répondre NoRoute. Un chauffeur égaré sans itinéraire est un
    // problème plus grave qu'un itinéraire qui aborde l'arrêt du mauvais côté : on rejoue la
    // chaîne sans contrainte plutôt que d'abandonner. Il verra l'arrêt et décidera.
    if (hasBearings(bearings)) {
        log("Routage", "Sens d'origine impossible à respecter — itinéraire libre.");
        const libre = await tryEngines(chunk, null, errors);
        if (libre) return libre;
    }
    throw new Error(errors.join(' · '));
}


function chunkWaypoints(waypoints, size) {
    const chunks = [];
    for (let i = 0; i < waypoints.length - 1; i += size - 1) {
        chunks.push(waypoints.slice(i, i + size));
    }
    return chunks;
}



// =====================================================================
//  RECALAGE D'UNE TRACE GPS SUR LES RUES  (service /match d'OSRM)
// =====================================================================
// Le tracé d'un enregistrement est une suite de fixes GPS bruts : il tremble, et il est
// structurellement décalé de 2 à 4 m parce que l'axe de rue dessiné sur la carte n'est pas
// la voie que le bus emprunte. Aucun filtre ne corrige ce biais — seul un recalage sur le
// réseau routier le fait.
//
// N'améliore que ce qu'on VOIT : un trajet enregistré reste la liste des arrêts, et c'est le
// routage qui reconstruit la géométrie au moment du guidage.

const OSRM_MATCH_ENDPOINTS = OSRM_ENDPOINTS.map(u => u.replace('/route/v1/', '/match/v1/'));

const OSRM_MATCH_MAX_POINTS = 100;   // limite du serveur public
const MATCH_MIN_CONFIDENCE = 0.3;    // en dessous, OSRM a rattaché la trace à la rue voisine
const MATCH_MIN_SPACING_M = 10;      // sous-échantillonnage
const MATCH_MAX_POINTS = 1000;       // plafond dur : 10 lots, pas 36
const MATCH_RADIUS_MIN_M = 10;
const MATCH_RADIUS_MAX_M = 50;

// Copie locale de la formule de geo.js : routing.js n'importe rien d'autre que maneuvers.js,
// et le sous-échantillonnage doit rester une fonction pure et testable.
function metersBetween(a, b) {
    const R = 6371000;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const la = a.lat * Math.PI / 180, lb = b.lat * Math.PI / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la) * Math.cos(lb) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Une tournée d'une heure à un fix par seconde fait 3600 points, soit 36 requêtes que la
// limitation de débit d'OSRM refusera. On garde un point tous les 10 m, et le DERNIER point
// quoi qu'il arrive — sans lui le tracé recalé s'arrêterait avant la fin du trajet.
function subsampleTrace(points, spacing, maxPoints) {
    const minSpacing = spacing || MATCH_MIN_SPACING_M;
    const cap = maxPoints || MATCH_MAX_POINTS;
    if (!Array.isArray(points) || points.length < 2) return [];

    const kept = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
        if (metersBetween(kept[kept.length - 1], points[i]) >= minSpacing) kept.push(points[i]);
    }
    kept.push(points[points.length - 1]);

    if (kept.length <= cap) return kept;
    // Encore trop long : on décime régulièrement, en préservant les deux extrémités.
    const step = Math.ceil(kept.length / cap);
    const thinned = kept.filter((_, i) => i % step === 0);
    if (thinned[thinned.length - 1] !== kept[kept.length - 1]) thinned.push(kept[kept.length - 1]);
    return thinned;
}

async function fetchMatchChunk(base, chunk) {
    const coords = chunk.map(p => `${p.lng},${p.lat}`).join(';');
    // radiuses par point : un fix à ±5 m et un fix à ±25 m ne méritent pas la même tolérance.
    // Sans ça OSRM rattache les mauvais points aussi fermement que les bons.
    const radiuses = chunk.map(p => {
        const a = typeof p.acc === 'number' && Number.isFinite(p.acc) ? p.acc : MATCH_RADIUS_MAX_M;
        return Math.round(Math.min(MATCH_RADIUS_MAX_M, Math.max(MATCH_RADIUS_MIN_M, a)));
    }).join(';');
    // tidy=true : OSRM nettoie les points redondants. gaps=ignore : une seconde de signal
    // perdu ne doit pas couper l'appariement en deux morceaux distincts.
    const url = `${base}${coords}?geometries=geojson&overview=full&tidy=true&gaps=ignore&radiuses=${radiuses}`;
    const res = await fetch(url, { signal: timeoutSignal(ROUTE_TIMEOUT_MS) });
    const data = await res.json();
    if (data.code !== 'Ok' || !data.matchings || !data.matchings.length) {
        throw new Error(data.message || data.code || 'réponse invalide');
    }
    const out = [];
    for (const m of data.matchings) {
        if (typeof m.confidence === 'number' && m.confidence < MATCH_MIN_CONFIDENCE) {
            throw new Error(`confiance trop faible (${m.confidence.toFixed(2)})`);
        }
        for (const c of m.geometry.coordinates) out.push([c[1], c[0]]);
    }
    return out;
}

// points : [{ lat, lng, acc? }, …]. Renvoie [[lat, lng], …], ou null si le recalage n'aboutit
// pas — l'appelant garde alors son tracé brut, il ne le perd JAMAIS.
async function matchTrace(points) {
    const sample = subsampleTrace(points);
    if (sample.length < 2) return null;

    const chunks = chunkWaypoints(sample, OSRM_MATCH_MAX_POINTS);
    const errors = [];

    for (const base of OSRM_MATCH_ENDPOINTS) {
        try {
            const out = [];
            for (let i = 0; i < chunks.length; i++) {
                const seg = await fetchMatchChunk(base, chunks[i]);
                // Chevauchement d'un point entre les lots (chunkWaypoints) : le premier point
                // du lot suivant est le dernier du précédent, on éviterait sinon un doublon.
                out.push(...(i === 0 ? seg : seg.slice(1)));
                // Pause de politesse : le serveur public limite le débit par IP.
                if (i < chunks.length - 1) await new Promise(r => setTimeout(r, 300));
            }
            log("Tracé", `Recalé sur ${out.length} points (depuis ${points.length} fixes).`);
            return out;
        } catch (e) {
            errors.push(`${base.split('/')[2]} : ${e.message}`);
        }
    }
    log("Tracé", `Recalage impossible — ${errors.join(' · ')}`);
    return null;
}

export {
    setRoutingLogger, OSRM_ENDPOINTS, VALHALLA_ENDPOINTS, ROUTE_TIMEOUT_MS, OSRM_MAX_WAYPOINTS,
    BEARING_TOLERANCE_DEG,
    timeoutSignal, fetchOsrmChunk, decodePolyline6, normalizeValhalla, fetchValhallaChunk,
    fetchRouteChunk, chunkWaypoints,
    OSRM_MATCH_ENDPOINTS, OSRM_MATCH_MAX_POINTS, MATCH_MIN_CONFIDENCE, MATCH_MIN_SPACING_M,
    MATCH_MAX_POINTS, subsampleTrace, fetchMatchChunk, matchTrace
};
